import { MathUtil } from "@base/math/MathUtil";
import { IVec2, Vec2 } from "@base/math/Vec2";
import { IVec3, Vec3 } from "@base/math/Vec3";

export class TriangleHelper {
  static calcHeightC(a: number, b: number, c: number): number | undefined {
    const s = (a + b + c) * 0.5;
    const inner = s * (s - a) * (s - b) * (s - c);
    if (inner < 0) {
      if (inner > -0.0000001)
        return 0;
      return undefined;
    }
    const A = Math.sqrt(inner);
    const hc = 2 * A / c;
    return hc;
  }

  static calcHeightCPointForDistances(pointA: IVec3, pointB: IVec3, distB: number, distA: number): IVec3 | undefined {
    const distC = Vec3.len(pointA, pointB);
    const h = TriangleHelper.calcHeightC(distA, distB, distC);
    if (h === undefined)
      return undefined;

    if (distB > distA) {
      const inner = distB ** 2 - h * h;
      if (inner < 0)
        return undefined;
      const b1 = Math.sqrt(inner);
      const pointC = Vec3.addScaled(pointA, Vec3.dir(pointA, pointB), b1);
      return pointC;
    } else {
      const inner = distA ** 2 - h * h;
      if (inner < 0)
        return undefined;
      const b1 = Math.sqrt(inner);
      const pointC = Vec3.addScaled(pointB, Vec3.dir(pointA, pointB), -b1);
      return pointC;
    }
  }

  static calcHeightCPointForDistances2(pointA: IVec2, pointB: IVec2, distB: number, distA: number, right?: boolean): IVec2 | undefined {
    const distC = Vec2.len(pointA, pointB);
    const h = TriangleHelper.calcHeightC(distA, distB, distC);
    if (h === undefined)
      return undefined;

    const n = Vec2.normalized(Vec2.normalVector(pointA, pointB, right));

    if (distB > distA) {
      const inner = distB ** 2 - h * h;
      if (inner < 0)
        return undefined;
      const b1 = Math.sqrt(inner);
      const pointC = Vec2.addScaled(pointA, Vec2.dir(pointA, pointB), b1);
      return Vec2.addScaled(pointC, n, h);
    } else {
      const inner = distA ** 2 - h * h;
      if (inner < 0)
        return undefined;
      const b1 = Math.sqrt(inner);
      const pointC = Vec2.addScaled(pointB, Vec2.dir(pointA, pointB), -b1);
      return Vec2.addScaled(pointC, n, h);
    }
  }

  static calcPointForDistancesWithY(pointA: IVec3, pointB: IVec3, distB: number, distA: number, targetY: number, right?: boolean): IVec3 | undefined {
    { // docs/images/calcPointForDistancesWithY_01.jpg
      const origin = pointA;
      const A = Vec3.sub(pointA, origin);
      const B = Vec3.sub(pointB, origin);
      const F = TriangleHelper.calcHeightCPointForDistances(A, B, distB, distA);
      if (F === undefined)
        return undefined;
      const n = Vec3.dir(A, B);

      const ty = targetY - origin.y;
      const dl = Vec3.dot(n, F) - ty * n.y;
      const a = n.z ** 2 + n.x ** 2;
      if (Math.abs(n.z) > Math.abs(n.x)) {
        const b = -2 * dl * n.x;
        const c = dl ** 2 + (ty ** 2 - distB ** 2) * n.z ** 2;
        const s = MathUtil.solveQuadratic(a, b, c).map((x) => ({ x, z: (dl - n.x * x) / n.z }))
        const r = s[right ? 1 : 0];
        return Vec3.from(r.x + origin.x, targetY, r.z + origin.z);
      } else {
        const b = -2 * dl * n.z;
        const c = dl ** 2 + (ty ** 2 - distB ** 2) * n.x ** 2;
        const s = MathUtil.solveQuadratic(a, b, c).map((z) => ({ x: (dl - n.z * z) / n.x, z }))
        const r = s[right ? 0 : 1];
        return Vec3.from(r.x + origin.x, targetY, r.z + origin.z);
      }

    }
  }

  static calcPointForDistancesY2(pointA: IVec2, pointB: IVec2, distB: number, distA: number, right?: boolean): IVec2 | undefined {
    { // docs/images/calcPointForDistancesWithY_01.jpg
      const F = TriangleHelper.calcHeightCPointForDistances2(pointA, pointB, distB, distA, right);
      if (F === undefined)
        return undefined;
      return F;
    }
  }

  static calcArea(a: number, b: number, c: number): number {
    const s = (a + b + c) / 2;
    const area = Math.sqrt(s * (s - a) * (s - b) * (s - c));
    return area;
  }

  static calcHalfVector2(p: IVec2, a1: IVec2, a2: IVec2, options?: { correctDir?: "right" | "left" }): IVec2 {
    const a1a2Dir = Vec2.dir(a1, a2);
    const pa1Dir = Vec2.dir(p, a1);
    const pa2Dir = Vec2.dir(p, a2);
    const addedDir = Vec2.add(pa1Dir, pa2Dir);
    let halfDir = Vec2.normalized(Vec2.equals(addedDir, Vec2.ZERO) ? Vec2.normalVector(a1a2Dir, true) : addedDir);

    if (options?.correctDir) {
      const n = Vec2.normalVector(a1a2Dir, true);
      const dot = Vec2.dot(n, halfDir);
      if ((options.correctDir === "right" && dot > 0) || (options.correctDir === "left" && dot < 0)) {
        halfDir = Vec2.negate(halfDir);
      }
    }
    return halfDir;
  }

  static calcHalfVector(p: IVec3, a1: IVec3, a2: IVec3, options?: { correctDir?: "right" | "left" }): IVec3 {
    return TriangleHelper.calcHalfVectorExtended(p, a1, a2, options).halfDir;
  }

  static calcHalfVectorExtended(p: IVec3, a1: IVec3, a2: IVec3, options?: { correctDir?: "right" | "left" }): { halfDir: IVec3, pa1Dir: IVec3, pa2Dir: IVec3, a1a2Dir: IVec3 } {
    const a1a2Dir = Vec3.dir(a1, a2);
    const pa1Dir = Vec3.dir(p, a1);
    const pa2Dir = Vec3.dir(p, a2);
    const addedDir = Vec3.add(pa1Dir, pa2Dir);
    let halfDir = Vec3.normalized(Vec3.equals(addedDir, Vec3.ZERO) ? Vec3.crossNormalized(pa1Dir, Vec3.Y) : addedDir);

    if (options?.correctDir) {
      const n = Vec3.crossNormalized(a1a2Dir, Vec3.Y);
      const dot = Vec3.dot(n, halfDir);
      if ((options.correctDir === "right" && dot > 0) || (options.correctDir === "left" && dot < 0)) {
        halfDir = Vec3.negate(halfDir);
      }
    }
    return { halfDir, pa1Dir, pa2Dir, a1a2Dir }
  }
}